import { Icon } from '@eventbrite/valkyrie-eds-icon';
import { ArrowLeftChunky, ArrowRightChunky } from '@eventbrite/valkyrie-eds-iconography';
import React, { useCallback, useRef, useState } from 'react';
import { LISTING_HEAP_IDS } from '../../_shared/constants';
import { FetchPriorityType, HeroImage } from '../HeroImage';
import { HeroVideo } from '../HeroVideo';
import styles from './HeroCarousel.module.scss';
import type { CarouselItems } from './index';
import { useKeyPress } from './useKeyPress';
import { useSwipe } from './useSwipe';

interface HeroCarousel {
  eventName: string;
  items: Array<CarouselItems>;
  initialSlide?: number;
  enableAutoSlide?: boolean;
  featuredVideo?: FeaturedVideoProps | undefined;
  nativeVideo?: NativeVideoProps;
}

export type FeaturedVideoProps = {
  url?: string;
  embedUrl: string;
  thumbnail?: {
    large?: {
      url: string;
    };
    medium?: {
      url: string;
    };
    small?: {
      url: string;
    };
  };
  provider?: string;
};

export type NativeVideoProps = {
  url: string;
  thumbnail: string;
};

const TRANSLATION_UNIT_INIT = '0';

const getSlidesTranslationValue = (slidePosition: number) => {
  return slidePosition === 0 ? TRANSLATION_UNIT_INIT : `-${slidePosition}00`;
};

export const HeroCarousel: React.FC<HeroCarousel> = ({
  eventName,
  items,
  initialSlide = 0,
  featuredVideo,
  nativeVideo,
}) => {
  const shouldShowCarouselControls = items.length > 1;
  const firstSlide = 0;
  const lastSlide = items.length - 1;
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [translationUnit, setTranslationUnit] = useState(getSlidesTranslationValue(initialSlide));
  const [isCarouselFocused, setIsCarouselFocused] = useState(false);
  const carousel = useRef(null);

  const goToSlide = useCallback((slideNumber: number) => {
    setCurrentSlide(slideNumber);
    setTranslationUnit(getSlidesTranslationValue(slideNumber));
  }, []);

  const nextSlide = useCallback(() => {
    const nextSlide = currentSlide === lastSlide ? firstSlide : currentSlide + 1;
    goToSlide(nextSlide);
  }, [currentSlide, lastSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    const prevSlide = currentSlide === firstSlide ? lastSlide : currentSlide - 1;
    goToSlide(prevSlide);
  }, [currentSlide, lastSlide, goToSlide]);

  const handleOnFocus = () => {
    if (!isCarouselFocused) {
      setIsCarouselFocused(true);
    }
  };
  const handleOnBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const isCarouselBlurred = !event.currentTarget.contains(event.relatedTarget as Node);
    if (isCarouselBlurred) {
      setIsCarouselFocused(false);
    }
  };

  useKeyPress({
    isCarouselFocused,
    onLeftKeyPressed: () => prevSlide(),
    onRightKeyPressed: () => nextSlide(),
  });

  useSwipe({
    ref: carousel,
    onSwipeLeft: () => nextSlide(),
    onSwipeRight: () => prevSlide(),
  });

  return (
    <div
      ref={carousel}
      tabIndex={shouldShowCarouselControls ? 0 : -1}
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
      data-testid="hero-carousel"
      className={styles.carouselWrapper}
    >
      {shouldShowCarouselControls && (
        <>
          <ul>
            <li>
              <button
                onClick={prevSlide}
                className={`${styles.controlButton} ${styles.prev}`}
                data-heap-id={LISTING_HEAP_IDS.LEFT_ARROW_HERO_CAROUSEL}
              >
                <Icon type={<ArrowLeftChunky />} />
                {/* translate */}
                <span className={styles.accessibleText}>{'Previous slide'}</span>
              </button>
            </li>
            <li>
              <button
                onClick={nextSlide}
                className={`${styles.controlButton} ${styles.next}`}
                data-heap-id={LISTING_HEAP_IDS.RIGHT_ARROW_HERO_CAROUSEL}
              >
                <Icon type={<ArrowRightChunky />} />
                {/* translate */}
                <span className={styles.accessibleText}>{'Next slide'}</span>
              </button>
            </li>
          </ul>
          <div className={styles.sliderBar}>
            <ul className={styles.sliderBarList}>
              {items.map((_, index: number) => {
                const slideNumber = index + 1;

                return (
                  <li key={index} data-testid="slider-bar">
                    <button
                      onClick={() => {
                        goToSlide(index);
                      }}
                      className={`${styles.sliderBarButton} ${index === currentSlide ? styles.active : ''}`}
                      aria-current={index === currentSlide}
                    >
                      {/* translate */}
                      <span className={styles.accessibleText}>{`Go to slide ${slideNumber}`}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
      {items.length > 0 ? (
        <div className={styles.carouselViewport}>
          <div
            className={styles.imageList}
            style={{ transform: `translateX(${translationUnit}%)` }}
            data-testid="slide-list"
          >
            {items.map((item, index: number) => {
              const shouldShowHeroVideo = index === 0 && (nativeVideo?.url || featuredVideo?.embedUrl);

              return (
                <div
                  className={styles.imageListItem}
                  data-testid="slide"
                  aria-hidden={!(index === currentSlide)}
                  key={`slide-${index}`}
                >
                  {shouldShowHeroVideo ? (
                    <HeroVideo featuredVideo={featuredVideo} nativeVideo={nativeVideo} overrideThumbnail={item} />
                  ) : (
                    <HeroImage
                      croppedLogoUrl480={item.croppedLogoUrl480}
                      croppedLogoUrl600={item.croppedLogoUrl600}
                      croppedLogoUrl940={item.croppedLogoUrl940}
                      croppedLogoUrl1880={item.croppedLogoUrl1880}
                      alt={index === 0 ? eventName : ''}
                      loading={index !== 0 ? 'lazy' : undefined}
                      fetchPriority={index === 0 ? FetchPriorityType.high : FetchPriorityType.low}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <HeroVideo nativeVideo={nativeVideo} featuredVideo={featuredVideo} />
      )}
    </div>
  );
};
